import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ActionIcon,
  Anchor,
  Box,
  Burger,
  Button,
  Container,
  Drawer,
  Group,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
  Accordion,
  Badge,
  Card,
  SegmentedControl
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useIsMobile } from '../../hooks/useResponsive'
import { notifications } from '@mantine/notifications'
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTwitter,
  IconBrandWhatsapp,
  IconCalendarEvent,
  IconLogin,
  IconMail,
  IconMapPin,
  IconPlayerPlayFilled,
  IconX
} from '@tabler/icons-react'
import { IconHeart, IconUserPlus } from '../../icons'
import siteSnapshot from '../../generated/site-snapshot.json'
import { DAY_LABELS, buildSiteGraph, formatTime12h, jsonForScript } from '../../lib/site-prerender'

// ============ Types ============

interface SiteContent {
  hero_title: string | null
  hero_subtitle: string | null
  hero_image_r2_key: string | null
  hero_image_url: string | null
  about_title: string | null
  about_body: string | null
  about_image_r2_key: string | null
  contact_address: string | null
  contact_phone: string | null
  contact_email: string | null
  contact_maps_embed_url: string | null
  social_facebook: string | null
  social_instagram: string | null
  social_twitter: string | null
  parroquia_url: string | null
  mass_section_title: string | null
  mass_section_subtitle: string | null
  mass_section_image_r2_key: string | null
  mass_section_image_url: string | null
  hours_section_title: string | null
  hours_section_subtitle: string | null
  hours_section_image_r2_key: string | null
  hours_section_image_url: string | null
  donate_cta_label: string | null
  volunteer_cta_label: string | null
}

interface EventCard {
  id: string
  title: string
  body: string | null
  cover_image_r2_key: string | null
  cover_image_url: string | null
  gallery_images: any[] | null
  youtube_url: string | null
  event_date: string | null
  location: string | null
}

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string | null
  cover_image_r2_key: string | null
  cover_image_url: string | null
  youtube_url: string | null
  published_at: string | null
}

interface Faq {
  id: string
  question: string
  answer: string
}

interface MassItem {
  id: string
  kind: 'semanal' | 'especial'
  day_of_week: number | null
  specific_date: string | null
  time: string
  description: string | null
}

interface HourItem {
  id: string
  day_of_week: number
  opens_at: string
  closes_at: string
  display_order: number
}

// ============ Helpers ============

const BRAND_GRADIENT = 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'

function youtubeId(url: string | null): string | null {
  if (!url) return null
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/)
  return m ? m[1] : null
}

function whatsappUrl(phone: string | null): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  // Assume Mexican number; prefix with country code 52 if missing
  const intl = digits.startsWith('52') ? digits : `52${digits}`
  return `https://wa.me/${intl}`
}

function formatEventDate(dateString: string | null): string {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
  } catch { return '' }
}

// ============ Lead capture modal ============

interface LeadModalProps {
  opened: boolean
  onClose: () => void
  defaultInterest: 'donar' | 'voluntario' | 'otro'
}

const LeadModal: React.FC<LeadModalProps> = ({ opened, onClose, defaultInterest }) => {
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' })
  const [interest, setInterest] = useState<'donar' | 'voluntario' | 'otro'>(defaultInterest)
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({})
  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setInterest(defaultInterest) }, [defaultInterest, opened])
  useEffect(() => { if (!opened) setErrors({}) }, [opened])

  const emailLooksValid = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())

  const setField = (key: keyof typeof form, value: string) => {
    setForm(f => ({ ...f, [key]: value }))
    // Clear the field's error as soon as the person starts fixing it.
    setErrors(e => (e[key as keyof typeof e] ? { ...e, [key]: undefined } : e))
  }

  /** Validation lives next to the fields, not in a toast that disappears. */
  const validate = () => {
    const next: { name?: string; email?: string; phone?: string } = {}
    if (!form.name.trim()) next.name = 'Necesitamos saber cómo te llamas.'
    if (interest === 'donar') {
      if (!form.email.trim()) next.email = 'Para coordinar tu donación necesitamos tu correo.'
      else if (!emailLooksValid(form.email)) next.email = 'Revisa el correo: debe verse como tu@correo.com'
    } else if (!form.email.trim() && !form.phone.trim()) {
      next.email = 'Déjanos un correo o un teléfono para responderte.'
      next.phone = 'Déjanos un correo o un teléfono para responderte.'
    } else if (form.email.trim() && !emailLooksValid(form.email)) {
      next.email = 'Revisa el correo: debe verse como tu@correo.com'
    }
    return next
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const found = validate()
    setErrors(found)
    if (Object.keys(found).length > 0) {
      // Send focus to the first field that needs attention.
      const first = found.name ? nameRef : found.email ? emailRef : phoneRef
      first.current?.focus()
      return
    }
    setSubmitting(true)
    try {
      const response = await fetch('/api/site/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Trim so a trailing space pasted from another app never reaches the DB.
        body: JSON.stringify({
          ...form,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
          interest
        })
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error || 'No se pudo enviar')
      }
      notifications.show({
        title: '¡Gracias!',
        message: 'Recibimos tu mensaje y te contactaremos pronto.',
        color: 'green'
      })
      setForm({ name: '', email: '', phone: '', message: '', website: '' })
      setErrors({})
      onClose()
    } catch (e) {
      notifications.show({
        title: 'Error',
        message: e instanceof Error ? e.message : 'No se pudo enviar',
        color: 'red'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const title =
    interest === 'donar' ? 'Quiero donar' :
    interest === 'voluntario' ? 'Quiero ser voluntario' :
    'Quiero ayudar'

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconHeart size={20} />
          <Text fw={700}>{title}</Text>
        </Group>
      }
      centered
      size="md"
      className="landing-modal"
    >
      <Stack gap="md" component="form" onSubmit={handleSubmit}>
        <Text size="sm" c="var(--text-2)">
          Déjanos tus datos y un voluntario se pondrá en contacto contigo.
        </Text>
        {/* A real radio group: arrow keys move between options, not a row of
            divs that a keyboard can never reach. */}
        <SegmentedControl
          fullWidth
          value={interest}
          onChange={(v) => setInterest(v as 'donar' | 'voluntario' | 'otro')}
          data={[
            { value: 'donar', label: 'Donar' },
            { value: 'voluntario', label: 'Voluntariado' },
            { value: 'otro', label: 'Otro' }
          ]}
        />
        {/* Honeypot — invisible to humans, attractive to spam bots. The server
            silently discards submissions where this field is filled. */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
          <label>
            Website
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => {
                const v = e.currentTarget.value
                setForm(f => ({ ...f, website: v }))
              }}
            />
          </label>
        </div>
        <TextInput
          ref={nameRef}
          label="Nombre completo"
          name="name"
          autoComplete="name"
          required
          error={errors.name}
          value={form.name}
          onChange={(e) => setField('name', e.currentTarget.value)}
          placeholder="Tu nombre…"
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            ref={emailRef}
            label="Correo"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            spellCheck={false}
            autoCapitalize="off"
            required={interest === 'donar'}
            error={errors.email}
            value={form.email}
            onChange={(e) => setField('email', e.currentTarget.value)}
            placeholder="tu@correo.com"
          />
          <TextInput
            ref={phoneRef}
            label="Teléfono"
            type="tel"
            name="tel"
            autoComplete="tel"
            inputMode="tel"
            spellCheck={false}
            error={errors.phone}
            value={form.phone}
            onChange={(e) => setField('phone', e.currentTarget.value)}
            placeholder="55 1234 5678"
          />
        </SimpleGrid>
        <Textarea
          label="Mensaje (opcional)"
          name="message"
          value={form.message}
          onChange={(e) => setField('message', e.currentTarget.value)}
          onKeyDown={(e) => {
            // Enter adds a line here, so ⌘/Ctrl+Enter is the send shortcut.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmit() }
          }}
          rows={3}
          placeholder="Cuéntanos cómo te gustaría apoyar…"
        />
        <Group justify="flex-end" mt="xs">
          <Button type="button" variant="subtle" color="gray" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting} color="blue">
            Enviar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ============ Top nav ============

interface TopNavProps {
  onOpenLead: (interest: 'donar' | 'voluntario') => void
  content: SiteContent | null
}

const TopNav: React.FC<TopNavProps> = ({ onOpenLead, content }) => {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, mobile] = useDisclosure(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const sections = [
    { id: 'acerca', label: 'Acerca de' },
    { id: 'horarios', label: 'Horarios' },
    { id: 'misas', label: 'Misas' },
    { id: 'eventos', label: 'Eventos' },
    { id: 'blog', label: 'Blog' },
    { id: 'faq', label: 'Preguntas' },
    { id: 'contacto', label: 'Contacto' }
  ]

  const scrollTo = (id: string) => {
    mobile.close()
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <Box
      component="header"
      className="landing-on-dark"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 50,
        background: scrolled ? 'rgba(30, 60, 114, 0.96)' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        boxShadow: scrolled ? '0 4px 16px rgba(0,0,0,0.18)' : 'none',
        transition: 'background-color 240ms ease, box-shadow 240ms ease, backdrop-filter 240ms ease',
        color: 'white'
      }}
    >
      <Container size="lg">
        <Group justify="space-between" align="center" py={scrolled ? 'xs' : 'sm'} wrap="nowrap">
          <UnstyledButton onClick={() => scrollTo('hero')} style={{ color: 'white' }}>
            <Group gap="xs" wrap="nowrap">
              <Box
                style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 4
                }}
              >
                <img src="/logocaritas.png" alt="Cáritas La Esperanza"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </Box>
              <div>
                <Text size="sm" fw={700} c="white">Cáritas</Text>
                <Text size="xs" c="rgba(255,255,255,0.8)" mt={-4}>La Esperanza</Text>
              </div>
            </Group>
          </UnstyledButton>

          {/* Desktop nav */}
          <Group gap="lg" visibleFrom="md" wrap="nowrap">
            {sections.map(s => (
              <UnstyledButton key={s.id} onClick={() => scrollTo(s.id)}
                style={{ color: 'white', fontWeight: 500, fontSize: 14 }}>
                {s.label}
              </UnstyledButton>
            ))}
          </Group>

          <Group gap="xs" wrap="nowrap">
            {/* Desktop: full label */}
            <Button
              component={Link}
              to="/dashboard"
              size="md"
              visibleFrom="md"
              leftSection={<IconLogin size={18} />}
              styles={{
                root: {
                  background: 'white',
                  color: '#1e3c72',
                  fontWeight: 700,
                  border: '2px solid white',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.12)'
                }
              }}
            >
              Sistema de medicamentos
            </Button>

            {/* Mobile: compact pill so the hamburger has room */}
            <Button
              component={Link}
              to="/dashboard"
              size="sm"
              hiddenFrom="md"
              leftSection={<IconLogin size={16} />}
              styles={{
                root: {
                  background: 'white',
                  color: '#1e3c72',
                  fontWeight: 700,
                  height: 38,
                  paddingLeft: 12,
                  paddingRight: 12
                }
              }}
            >
              Sistema
            </Button>

            {/* Hamburger — wrapped in a white-tinted pill so it pops on the gradient */}
            <Burger
              opened={mobileOpen}
              onClick={mobile.toggle}
              hiddenFrom="md"
              size="md"
              color="white"
              aria-label="Abrir menú"
              style={{
                background: 'rgba(255,255,255,0.18)',
                borderRadius: 8,
                padding: 4,
                border: '1px solid rgba(255,255,255,0.32)'
              }}
            />
          </Group>
        </Group>
      </Container>

      <Drawer opened={mobileOpen} onClose={mobile.close} hiddenFrom="md" position="right" size="xs"
        className="landing-drawer landing-on-dark"
        styles={{
          body: { background: BRAND_GRADIENT, height: '100%', color: 'white', padding: 16 },
          content: { background: BRAND_GRADIENT }
        }}
        withCloseButton={false}
      >
        <Stack gap="md">
          <Group justify="space-between" mb="xs">
            <Text fw={700} size="lg">Menú</Text>
            <ActionIcon variant="subtle" color="white" onClick={mobile.close} aria-label="Cerrar" size="lg">
              <IconX size={22} stroke={2.2} color="white" />
            </ActionIcon>
          </Group>

          {/* Section links */}
          <Stack gap={4}>
            {sections.map(s => (
              <UnstyledButton
                key={s.id}
                onClick={() => scrollTo(s.id)}
                style={{
                  color: 'white',
                  padding: '14px 12px',
                  fontWeight: 500,
                  fontSize: 16,
                  borderRadius: 8,
                  minHeight: 44
                }}
              >
                {s.label}
              </UnstyledButton>
            ))}
          </Stack>

          {/* Primary CTAs */}
          <Stack gap="xs" mt="sm">
            <Button
              component={Link}
              to="/dashboard"
              size="md"
              leftSection={<IconLogin size={16} />}
              styles={{
                root: { background: 'white', color: '#1e3c72', fontWeight: 700 },
                label: { fontSize: 13.5, letterSpacing: '-0.01em' },
                section: { marginInlineEnd: 6 }
              }}
              fullWidth
            >
              Sistema de medicamentos
            </Button>
            <Button
              size="md"
              variant="white"
              color="blue"
              leftSection={<IconUserPlus size={16} />}
              onClick={() => { mobile.close(); onOpenLead('voluntario') }}
              styles={{
                root: { fontWeight: 700 },
                label: { fontSize: 13.5 },
                section: { marginInlineEnd: 6 }
              }}
              fullWidth
            >
              Quiero ser voluntario
            </Button>
            <Button
              size="md"
              color="pink"
              leftSection={<IconHeart size={16} />}
              onClick={() => { mobile.close(); onOpenLead('donar') }}
              styles={{
                root: { fontWeight: 700 },
                label: { fontSize: 13.5 },
                section: { marginInlineEnd: 6 }
              }}
              fullWidth
            >
              Quiero donar
            </Button>
          </Stack>

          {/* Quick contact actions (tap to call / mail) */}
          {(content?.contact_phone || content?.contact_email) && (
            <Stack gap={4} mt="sm" pt="md" style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Text size="xs" fw={700} tt="uppercase" c="rgba(255,255,255,0.6)" style={{ letterSpacing: '0.08em' }}>
                Contáctanos directo
              </Text>
              {content?.contact_phone && (
                <Anchor
                  href={whatsappUrl(content.contact_phone)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 4px',
                    minHeight: 44,
                    fontSize: 15,
                    fontWeight: 500
                  }}
                  underline="never"
                >
                  <IconBrandWhatsapp size={20} />
                  WhatsApp · {content.contact_phone}
                </Anchor>
              )}
              {content?.contact_email && (
                <Anchor
                  href={`mailto:${content.contact_email}`}
                  style={{
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 4px',
                    minHeight: 44,
                    fontSize: 14,
                    fontWeight: 500,
                    minWidth: 0,
                    overflowWrap: 'anywhere'
                  }}
                  underline="never"
                >
                  <IconMail size={20} style={{ flexShrink: 0 }} />
                  {content.contact_email}
                </Anchor>
              )}
            </Stack>
          )}
        </Stack>
      </Drawer>
    </Box>
  )
}

// ============ Section helpers ============

/**
 * Section heading. The heading carries its own weight — no uppercase kicker
 * above it, and the hairline below does the work of announcing a new section.
 */
const SectionHeading: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Stack gap="sm" mb={40} className="r-item">
    <Title order={2} className="landing-heading">{title}</Title>
    {subtitle && <Text component="p" size="md" className="landing-sub">{subtitle}</Text>}
    <hr className="landing-rule" style={{ marginTop: 8, width: '100%' }} />
  </Stack>
)

/** Ink panel used wherever an admin has not uploaded a section image yet. */
const ImagePanel: React.FC<{ title: string; note?: string; minHeight?: number; className?: string }> = ({ title, note, minHeight, className }) => (
  <Box className={`landing-fallback${className ? ' ' + className : ''}`} style={minHeight ? { minHeight } : undefined}>
    <Text component="span" className="landing-fallback__title">{title}</Text>
    {note && <Text component="span" className="landing-fallback__note">{note}</Text>}
  </Box>
)

// ============ Hero ============

const Hero: React.FC<{ content: SiteContent | null; onOpenLead: (i: 'donar' | 'voluntario') => void }> = ({ content, onOpenLead }) => {
  const hasImage = !!content?.hero_image_url
  return (
    <Box
      id="hero"
      component="section"
      className="landing-on-dark"
      style={{
        background: BRAND_GRADIENT,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: 'clamp(140px, 18vh, 200px)',
        paddingBottom: 'clamp(80px, 12vh, 140px)',
        // Full viewport, image or not. min-height so a short landscape phone
        // lets the padded content grow past the fold instead of clipping it.
        minHeight: '100svh',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Uploaded hero image — eager-loaded + high priority so it doesn't
          starve waiting for JS-driven layout. */}
      {hasImage && (
        <img
          src={content!.hero_image_url!}
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
        />
      )}
      {/* Legibility overlay — darker at the bottom so text stays readable */}
      <Box
        style={{
          position: 'absolute', inset: 0,
          background: hasImage
            ? 'linear-gradient(180deg, rgba(15, 29, 58, 0.45) 0%, rgba(15, 29, 58, 0.35) 40%, rgba(15, 29, 58, 0.78) 100%)'
            : 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.10), transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,191,255,0.18), transparent 55%)',
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      <Container size="lg" style={{ position: 'relative', zIndex: 2, width: '100%' }}>
        <Stack gap="lg" align="center" ta="center" maw={780} mx="auto">
          <Title
            order={1}
            className="landing-hero-in"
            translate="no"
            style={{
              fontSize: 'clamp(36px, 6vw, 64px)',
              lineHeight: 1.04,
              letterSpacing: '-0.035em',
              fontWeight: 800,
              color: 'white',
              textWrap: 'balance',
              textShadow: hasImage ? '0 2px 24px rgba(0,0,0,0.45)' : undefined
            }}
          >
            {content?.hero_title || 'Cáritas La Esperanza'}
          </Title>
          <Text
            size="lg"
            className="landing-hero-in landing-hero-in--2"
            c="rgba(255,255,255,0.95)"
            maw={620}
            style={{ lineHeight: 1.55, ...(hasImage ? { textShadow: '0 1px 16px rgba(0,0,0,0.55)' } : {}) }}
          >
            {content?.hero_subtitle || 'Apoyamos a familias vulnerables con medicamentos, acompañamiento y dignidad humana.'}
          </Text>
          <Group gap="sm" mt="md" wrap="wrap" justify="center" className="landing-hero-in landing-hero-in--3">
            <Button
              size="lg"
              variant="white"
              color="blue"
              leftSection={<IconUserPlus size={20} />}
              onClick={() => onOpenLead('voluntario')}
              styles={{ root: { fontWeight: 700 } }}
            >
              Quiero ser voluntario
            </Button>
            <Button
              size="lg"
              variant="outline"
              color="white"
              leftSection={<IconCalendarEvent size={20} />}
              onClick={() => {
                const el = document.getElementById('eventos')
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }}
              styles={{ root: { fontWeight: 700, color: 'white', borderColor: 'rgba(255,255,255,0.85)' } }}
            >
              Ver eventos
            </Button>
          </Group>
        </Stack>
      </Container>
    </Box>
  )
}

// ============ About ============

const AboutSection: React.FC<{ content: SiteContent | null; onZoomImage: (url: string) => void }> = ({ content, onZoomImage }) => {
  if (!content?.about_body) return null
  const aboutUrl = (content as any).about_image_url as string | null | undefined
  return (
    <Box id="acerca" component="section" className="reveal-section landing-section">
      <Container size="lg">
        <SectionHeading title={content.about_title || 'Quiénes somos'} />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="xl">
          <div className="r-item">
            <Text size="lg" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {content.about_body}
            </Text>
          </div>
          {aboutUrl ? (
            <button
              type="button"
              className="landing-zoom r-right"
              onClick={() => onZoomImage(aboutUrl)}
              aria-label={`Ampliar imagen: ${content.about_title || 'Quiénes somos'}`}
            >
              <img
                src={aboutUrl}
                alt={content.about_title || 'Quiénes somos'}
                style={{ width: '100%', height: '100%', minHeight: 240, objectFit: 'cover', display: 'block' }}
                loading="lazy"
              />
            </button>
          ) : (
            <ImagePanel
              className="r-right"
              title="Con corazón abierto"
              note="Gracias a la generosidad de donantes y voluntarios"
              minHeight={240}
            />
          )}
        </SimpleGrid>
      </Container>
    </Box>
  )
}

// ============ Unified section grid (mobile + desktop) ============

/**
 * Renders a list of cards:
 *  - Mobile (≤md), not expanded: horizontal-paginated grid; each page is a
 *    2-row × 3-col grid of 6 cards. Scroll-snap between pages.
 *  - Desktop (≥md), not expanded: vertical 3-col grid showing the first 6.
 *  - When items.length > 6, both modes show "Ver más" below; clicking it
 *    expands to a full vertical grid revealing every item.
 */
const SectionGrid: React.FC<{
  items: any[]
  renderCard: (item: any, compact?: boolean) => React.ReactNode
}> = ({ items, renderCard }) => {
  const isMobile = useIsMobile()
  const [expanded, setExpanded] = useState(false)
  const PAGE_SIZE = 6
  const hasMore = items.length > PAGE_SIZE

  if (items.length === 0) return null

  // Mobile: 3 columns × 2 rows CSS grid, each column 40% of the viewport so
  // 2.5 columns peek and the 3rd card on top + 3rd on bottom always tease.
  // Cards fill row-major; if there are fewer than 6 items, the missing slots
  // are rendered as invisible spacers so the grid keeps its 3-column shape
  // and the scroll affordance is preserved even with 3-4 events.
  if (isMobile && !expanded) {
    const visible = items.slice(0, PAGE_SIZE) // up to 6 cards in the carousel
    const ROWS = 2
    const COLS = Math.max(3, Math.ceil(visible.length / ROWS))
    const cellCount = ROWS * COLS
    return (
      <>
        <Box
          className="mobile-carousel"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${COLS}, 40%)`,
            gridTemplateRows: `repeat(${ROWS}, auto)`,
            gap: 10,
            overflowX: 'auto',
            scrollSnapType: 'x proximity',
            paddingLeft: 16,
            paddingRight: 16,
            marginLeft: -16,
            marginRight: -16,
            paddingBottom: 8,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {Array.from({ length: cellCount }).map((_, idx) => {
            const item = visible[idx]
            const isRowStart = idx % COLS === 0
            return (
              <div
                key={item ? (item as any).id : `empty-${idx}`}
                style={{ scrollSnapAlign: isRowStart ? 'start' : undefined, minWidth: 0 }}
              >
                {item ? renderCard(item, true) : null}
              </div>
            )
          })}
        </Box>
        {hasMore && (
          <Group justify="center" mt="md">
            <Button variant="light" onClick={() => setExpanded(true)}>
              Ver más ({items.length - PAGE_SIZE})
            </Button>
          </Group>
        )}
      </>
    )
  }

  // Desktop OR expanded mobile: vertical 3-col grid (or 2-col on small screens)
  const visible = expanded ? items : items.slice(0, PAGE_SIZE)
  return (
    <>
      <SimpleGrid cols={{ base: 2, sm: 2, md: 3 }} spacing="md" className="r-stagger">
        {visible.map((item, idx) => (
          <React.Fragment key={(item as any).id || idx}>
            {renderCard(item)}
          </React.Fragment>
        ))}
      </SimpleGrid>
      {hasMore && (
        <Group justify="center" mt="md">
          {expanded ? (
            <Button variant="subtle" onClick={() => setExpanded(false)}>
              Mostrar menos
            </Button>
          ) : (
            <Button variant="light" onClick={() => setExpanded(true)}>
              Ver más ({items.length - PAGE_SIZE})
            </Button>
          )}
        </Group>
      )}
    </>
  )
}

// ============ Misas ============

const formatSpecificDate = (d: string | null): string => {
  if (!d) return ''
  try {
    return new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
  } catch { return '' }
}

const MisasSection: React.FC<{
  content: SiteContent | null
  masses: MassItem[]
  onZoomImage: (url: string) => void
}> = ({ content, masses, onZoomImage }) => {
  if (masses.length === 0 && !content?.mass_section_image_url) return null

  // Group weekly by day; collect specials separately
  const semanal: Record<number, MassItem[]> = {}
  const especiales: MassItem[] = []
  for (const m of masses) {
    if (m.kind === 'semanal' && m.day_of_week) {
      if (!semanal[m.day_of_week]) semanal[m.day_of_week] = []
      semanal[m.day_of_week].push(m)
    } else if (m.kind === 'especial') {
      especiales.push(m)
    }
  }
  Object.keys(semanal).forEach(d => {
    semanal[Number(d)].sort((a, b) => a.time.localeCompare(b.time))
  })

  const sectionImage = content?.mass_section_image_url

  return (
    <Box id="misas" component="section" className="reveal-section landing-section">
      <Container size="lg">
        <SectionHeading
          title={content?.mass_section_title || 'Misas y horarios'}
          subtitle={content?.mass_section_subtitle || undefined}
        />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="xl">
          {sectionImage ? (
            <button
              type="button"
              className="landing-zoom r-left"
              onClick={() => onZoomImage(sectionImage)}
              aria-label={`Ampliar imagen: ${content?.mass_section_title || 'Misas'}`}
            >
              <img
                src={sectionImage}
                alt={content?.mass_section_title || 'Misas'}
                style={{ width: '100%', height: '100%', minHeight: 280, objectFit: 'cover', display: 'block' }}
                loading="lazy"
              />
            </button>
          ) : (
            <ImagePanel className="r-left" title="Parroquia Perichurch" />
          )}

          <Stack gap="md">
            {Object.keys(semanal).length > 0 && (
              <div>
                <Title order={3} className="landing-subhead r-item">Horarios semanales</Title>
                <div className="landing-sched r-stagger">
                  {[1, 2, 3, 4, 5, 6, 7].filter(d => semanal[d]?.length).map(d => (
                    <div key={d} className="landing-sched__row">
                      <span className="landing-sched__day">{DAY_LABELS[d]}</span>
                      <span className="landing-sched__times">
                        {semanal[d].map(m => (
                          <span key={m.id} style={{ display: 'block' }}>
                            <span className="num" style={{ fontWeight: 600, color: 'var(--ink-900)' }}>
                              {formatTime12h(m.time)}
                            </span>
                            {m.description ? (
                              <span style={{ color: 'var(--text-2)' }}>{` · ${m.description}`}</span>
                            ) : null}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {especiales.length > 0 && (
              <div>
                <Title order={3} className="landing-subhead" style={{ marginTop: 8 }}>
                  Celebraciones especiales
                </Title>
                <div className="landing-sched landing-sched--accent r-stagger">
                  {especiales.map(m => (
                    <div key={m.id} className="landing-sched__row">
                      <span className="landing-sched__day num">{formatSpecificDate(m.specific_date)}</span>
                      <span className="landing-sched__times">
                        <span className="num" style={{ fontWeight: 600, color: 'var(--ink-900)' }}>
                          {formatTime12h(m.time)}
                        </span>
                        {m.description ? (
                          <span style={{ color: 'var(--text-2)' }}>{` · ${m.description}`}</span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Stack>
        </SimpleGrid>
      </Container>
    </Box>
  )
}

// ============ Horarios de operación ============

const HoursSection: React.FC<{
  content: SiteContent | null
  hours: HourItem[]
  onZoomImage: (url: string) => void
}> = ({ content, hours, onZoomImage }) => {
  if (hours.length === 0 && !content?.hours_section_image_url) return null

  // Group by day; sort each day's entries by opens_at
  const byDay: Record<number, HourItem[]> = {}
  for (const h of hours) {
    if (!byDay[h.day_of_week]) byDay[h.day_of_week] = []
    byDay[h.day_of_week].push(h)
  }
  Object.keys(byDay).forEach(d => {
    byDay[Number(d)].sort((a, b) => {
      if (a.display_order !== b.display_order) return a.display_order - b.display_order
      return a.opens_at.localeCompare(b.opens_at)
    })
  })

  const sectionImage = content?.hours_section_image_url
  const todayIndex = ((new Date().getDay() + 6) % 7) + 1 // Mon=1, Sun=7

  return (
    <Box id="horarios" component="section" className="reveal-section landing-section landing-section--tint">
      <Container size="lg">
        <SectionHeading
          title={content?.hours_section_title || 'Horarios de operación'}
          subtitle={content?.hours_section_subtitle || undefined}
        />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="xl">
          {sectionImage ? (
            <button
              type="button"
              className="landing-zoom r-left"
              onClick={() => onZoomImage(sectionImage)}
              aria-label={`Ampliar imagen: ${content?.hours_section_title || 'Horarios'}`}
            >
              <img
                src={sectionImage}
                alt={content?.hours_section_title || 'Horarios'}
                style={{ width: '100%', height: '100%', minHeight: 280, objectFit: 'cover', display: 'block' }}
                loading="lazy"
              />
            </button>
          ) : (
            <ImagePanel className="r-left" title="Cáritas La Esperanza" note="Centro comunitario" />
          )}

          <div>
            <Title order={3} className="landing-subhead r-item">Horario semanal</Title>
            <div className="landing-sched r-stagger">
              {[1, 2, 3, 4, 5, 6, 7].map(d => {
                const ranges = byDay[d] || []
                const isToday = d === todayIndex
                return (
                  <div
                    key={d}
                    className={`landing-sched__row${isToday ? ' landing-sched__row--today' : ''}`}
                  >
                    <span className="landing-sched__day">
                      {DAY_LABELS[d]}
                      {isToday && (
                        <Badge size="xs" color="blue" variant="light" ml={8} style={{ verticalAlign: 'middle' }}>
                          Hoy
                        </Badge>
                      )}
                    </span>
                    <span className="landing-sched__times">
                      {ranges.length === 0 ? (
                        <span style={{ color: 'var(--text-2)' }}>Cerrado</span>
                      ) : (
                        ranges.map(r => (
                          <span key={r.id} className="num" style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)' }}>
                            {formatTime12h(r.opens_at)} – {formatTime12h(r.closes_at)}
                          </span>
                        ))
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </SimpleGrid>
      </Container>
    </Box>
  )
}

// ============ Events ============

const renderEventCard = (ev: EventCard, onClick: () => void, compact = false) => {
  const ytId = youtubeId(ev.youtube_url)
  const aspect = compact ? '3/4' : '16/9'
  return (
    <Box
      key={ev.id}
      component="article"
      className="landing-card"
      style={{ height: compact ? 'auto' : '100%' }}
    >
      <div className="landing-card__media" style={{ aspectRatio: aspect }}>
        {ytId ? (
          <div style={{
            width: '100%', height: '100%',
            background: `url(https://i.ytimg.com/vi/${ytId}/hqdefault.jpg) center/cover no-repeat`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Box style={{ background: 'rgba(0,0,0,0.55)', borderRadius: '50%', padding: compact ? 8 : 12 }}>
              <IconPlayerPlayFilled size={compact ? 16 : 20} color="white" />
            </Box>
          </div>
        ) : ev.cover_image_url ? (
          <img src={ev.cover_image_url} alt={ev.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        ) : (
          <Stack align="center" justify="center" h="100%" gap={2} c="var(--text-2)">
            <IconCalendarEvent size={compact ? 22 : 28} />
            {!compact && <Text size="xs">Sin imagen</Text>}
          </Stack>
        )}
      </div>
      <Stack p={compact ? 'sm' : 'md'} gap={4}>
        {ev.event_date && (
          <Text size="xs" fw={600} c="var(--accent)" className="num" lineClamp={1}>
            {formatEventDate(ev.event_date)}
          </Text>
        )}
        <Text fw={700} size={compact ? 'sm' : 'md'} lineClamp={2} c="var(--ink-900)">
          <button type="button" className="landing-card__link" onClick={onClick}>{ev.title}</button>
        </Text>
        {!compact && ev.location && (
          <Group gap={4} c="var(--text-2)" wrap="nowrap">
            <IconMapPin size={14} style={{ flexShrink: 0 }} />
            <Text size="xs" lineClamp={1} style={{ minWidth: 0 }}>{ev.location}</Text>
          </Group>
        )}
        {!compact && ev.body && (
          <Text size="sm" c="var(--text-2)" lineClamp={2} mt={4}>
            {ev.body}
          </Text>
        )}
      </Stack>
    </Box>
  )
}

const EventsSection: React.FC<{ events: EventCard[]; onItemClick: (ev: EventCard) => void }> = ({ events, onItemClick }) => {
  if (events.length === 0) return null
  return (
    <Box id="eventos" component="section" className="reveal-section landing-section landing-section--tint">
      <Container size="lg">
        <SectionHeading title="Eventos y actividades"
          subtitle="Conoce lo que hacemos por la comunidad y únete a nuestras próximas actividades." />
        <SectionGrid items={events} renderCard={(ev, compact) => renderEventCard(ev as EventCard, () => onItemClick(ev as EventCard), compact)} />
      </Container>
    </Box>
  )
}

// ============ Blog ============

const renderBlogCard = (p: BlogPost, onClick: () => void, compact = false) => (
  <Box
    key={p.id}
    component="article"
    className="landing-card"
    style={{ height: compact ? 'auto' : '100%' }}
  >
    <div className="landing-card__media" style={{ aspectRatio: compact ? '3/4' : '16/9' }}>
      {p.cover_image_url ? (
        <img src={p.cover_image_url} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
      ) : (
        <Stack align="center" justify="center" h="100%" gap={2} c="var(--text-2)">
          <IconPlayerPlayFilled size={compact ? 22 : 28} />
          {!compact && <Text size="xs">Sin portada</Text>}
        </Stack>
      )}
    </div>
    <Stack p={compact ? 'sm' : 'md'} gap={4}>
      {p.published_at && (
        <Text size="xs" fw={600} c="var(--accent)" className="num" lineClamp={1}>
          {formatEventDate(p.published_at)}
        </Text>
      )}
      <Text fw={700} size={compact ? 'sm' : 'md'} lineClamp={2} c="var(--ink-900)">
        <button type="button" className="landing-card__link" onClick={onClick}>{p.title}</button>
      </Text>
      {!compact && p.excerpt && (
        <Text size="sm" c="var(--text-2)" lineClamp={2}>{p.excerpt}</Text>
      )}
    </Stack>
  </Box>
)

const BlogSection: React.FC<{ posts: BlogPost[]; onItemClick: (p: BlogPost) => void }> = ({ posts, onItemClick }) => {
  if (posts.length === 0) return null
  return (
    <Box id="blog" component="section" className="reveal-section landing-section">
      <Container size="lg">
        <SectionHeading title="Blog" subtitle="Reflexiones, testimonios y noticias de nuestra comunidad." />
        <SectionGrid items={posts} renderCard={(p, compact) => renderBlogCard(p as BlogPost, () => onItemClick(p as BlogPost), compact)} />
      </Container>
    </Box>
  )
}

// ============ FAQ ============

const FAQSection: React.FC<{ faqs: Faq[] }> = ({ faqs }) => {
  if (faqs.length === 0) return null
  return (
    <Box id="faq" component="section" className="reveal-section landing-section landing-section--tint">
      <Container size="lg">
        <SectionHeading title="Preguntas frecuentes" />
        <Accordion variant="separated" radius="md" className="r-stagger">
          {faqs.map(f => (
            <Accordion.Item key={f.id} value={f.id}>
              <Accordion.Control>
                <Text fw={600}>{f.question}</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" maw="68ch" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {f.answer}
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Container>
    </Box>
  )
}

// ============ Contact ============

const ContactSection: React.FC<{ content: SiteContent | null; onOpenLead: (i: 'donar' | 'voluntario') => void }> = ({ content, onOpenLead }) => {
  if (!content) return null
  return (
    <Box id="contacto" component="section" className="reveal-section landing-section">
      <Container size="lg">
        <SectionHeading title="Contacto" subtitle="Estamos para escucharte y servir a nuestra comunidad." />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Stack gap="md">
            {/* Label/value pairs are a description list — the same hairline
                language as the schedules, so the page reads as one system. */}
            <Box component="dl" className="landing-deflist r-stagger">
              {content.contact_address && (
                <div className="landing-deflist__row">
                  <dt>Dirección</dt>
                  <dd>
                    <IconMapPin size={16} aria-hidden="true" />
                    <span>{content.contact_address}</span>
                  </dd>
                </div>
              )}
              {content.contact_phone && (
                <div className="landing-deflist__row">
                  <dt>WhatsApp</dt>
                  <dd>
                    <IconBrandWhatsapp size={16} aria-hidden="true" />
                    <Anchor
                      href={whatsappUrl(content.contact_phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      fw={600}
                      c="var(--ink-700)"
                      className="num"
                    >
                      {content.contact_phone}
                    </Anchor>
                  </dd>
                </div>
              )}
              {content.contact_email && (
                <div className="landing-deflist__row">
                  <dt>Correo</dt>
                  <dd>
                    <IconMail size={16} aria-hidden="true" />
                    <Anchor href={`mailto:${content.contact_email}`} fw={600} c="var(--ink-700)">
                      {/* A long address may break after the @ (and anywhere, if it still does not fit) instead of widening the page */}
                      {content.contact_email.split('@').map((part, i) => (
                        <React.Fragment key={i}>{i > 0 && <>@<wbr /></>}{part}</React.Fragment>
                      ))}
                    </Anchor>
                  </dd>
                </div>
              )}
            </Box>
            <Group gap="sm" mt="md">
              <Button color="pink" leftSection={<IconHeart size={18} />} onClick={() => onOpenLead('donar')}>
                Quiero donar
              </Button>
              <Button variant="outline" color="blue" leftSection={<IconUserPlus size={18} />}
                onClick={() => onOpenLead('voluntario')}>
                Quiero ser voluntario
              </Button>
            </Group>
          </Stack>
          {content.contact_maps_embed_url && (
            <Card className="r-right" radius="lg" withBorder padding={0} style={{ overflow: 'hidden', minHeight: 280 }}>
              <iframe
                src={content.contact_maps_embed_url}
                title="Mapa"
                style={{ width: '100%', height: '100%', minHeight: 280, border: 0 }}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            </Card>
          )}
        </SimpleGrid>
      </Container>
    </Box>
  )
}

// ============ Footer ============

const Footer: React.FC<{ content: SiteContent | null }> = ({ content }) => (
  <Box component="footer" style={{ background: '#0f1d3a', color: 'white', paddingTop: 48, paddingBottom: 32 }}>
    <Container size="lg">
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
        <Stack gap="xs">
          <Group gap="xs">
            <Box style={{ width: 36, height: 36, borderRadius: 8, background: 'white', padding: 4 }}>
              <img src="/logocaritas.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <div>
              <Text fw={700}>Cáritas La Esperanza</Text>
              <Text size="xs" c="rgba(255,255,255,0.7)">Centro Comunitario</Text>
            </div>
          </Group>
          <Text size="sm" c="rgba(255,255,255,0.7)" mt="sm">
            Acompañamos a familias vulnerables con medicamentos y dignidad humana.
          </Text>
        </Stack>
        <Stack gap="xs">
          <Text fw={700} size="sm">Contacto</Text>
          {content?.contact_address && <Text size="sm" c="rgba(255,255,255,0.85)">{content.contact_address}</Text>}
          {content?.contact_phone && <Text size="sm" c="rgba(255,255,255,0.85)">{content.contact_phone}</Text>}
          {content?.contact_email && <Text size="sm" c="rgba(255,255,255,0.85)">{content.contact_email}</Text>}
        </Stack>
        <Stack gap="xs">
          <Text fw={700} size="sm">Síguenos</Text>
          <Stack gap={6}>
            {content?.social_facebook && (
              <Anchor
                href={content.social_facebook}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                c="rgba(255,255,255,0.9)"
                underline="never"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <IconBrandFacebook size={16} />
                Cáritas La Esperanza
              </Anchor>
            )}
            {content?.parroquia_url && (
              <Anchor
                href={content.parroquia_url}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                c="rgba(255,255,255,0.9)"
                underline="never"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <IconBrandFacebook size={16} />
                Parroquia Perichurch
              </Anchor>
            )}
            {content?.social_instagram && (
              <Anchor
                href={content.social_instagram}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                c="rgba(255,255,255,0.9)"
                underline="never"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <IconBrandInstagram size={16} />
                Instagram
              </Anchor>
            )}
            {content?.social_twitter && (
              <Anchor
                href={content.social_twitter}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                c="rgba(255,255,255,0.9)"
                underline="never"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <IconBrandTwitter size={16} />
                Twitter / X
              </Anchor>
            )}
          </Stack>
        </Stack>
      </SimpleGrid>
      <Text size="xs" c="rgba(255,255,255,0.55)" ta="center" mt="xl">
        © {new Date().getFullYear()} Centro Comunitario Cáritas La Esperanza. Todos los derechos reservados.
      </Text>
    </Container>
  </Box>
)

// ============ Detail modal & image zoom ============

type SelectedItem =
  | { type: 'event'; item: EventCard }
  | { type: 'post'; item: BlogPost }

interface DetailModalProps {
  selected: SelectedItem | null
  onClose: () => void
  onZoomImage: (url: string) => void
}

const DetailModal: React.FC<DetailModalProps> = ({ selected, onClose, onZoomImage }) => {
  const isMobile = useIsMobile()
  if (!selected) return null
  const isEvent = selected.type === 'event'
  const ev = isEvent ? selected.item as EventCard : null
  const post = !isEvent ? selected.item as BlogPost : null
  const ytId = youtubeId(isEvent ? (ev?.youtube_url || null) : (post?.youtube_url || null))
  const coverUrl = isEvent ? ev?.cover_image_url : post?.cover_image_url
  const title = isEvent ? ev!.title : post!.title
  const body = isEvent ? ev?.body : post?.body
  const dateString = isEvent
    ? (ev?.event_date ? formatEventDate(ev.event_date) : '')
    : (post?.published_at ? formatEventDate(post.published_at) : '')
  const location = isEvent ? ev?.location : null
  const galleryRaw: any[] = (isEvent ? ev?.gallery_images : post?.gallery_images) || []

  return (
    <Modal
      className="landing-modal"
      opened={true}
      onClose={onClose}
      size={isMobile ? '100%' : 'xl'}
      fullScreen={isMobile}
      centered
      padding={0}
      withCloseButton={false}
      styles={{
        body: { padding: 0 },
        content: { background: 'white' }
      }}
    >
      <Box style={{ position: 'relative' }}>
        {/* Close button — floating over the cover */}
        <ActionIcon
          variant="filled"
          color="dark"
          size="lg"
          radius="xl"
          onClick={onClose}
          aria-label="Cerrar"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)'
          }}
        >
          <IconX size={20} stroke={2.2} color="white" />
        </ActionIcon>

        {/* Cover / YouTube */}
        {ytId ? (
          <div style={{ aspectRatio: '16/9', background: 'black' }}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=0`}
              title={title}
              style={{ width: '100%', height: '100%', border: 0 }}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : coverUrl ? (
          <button
            type="button"
            className="landing-zoom"
            style={{ aspectRatio: '16/9', background: 'var(--ink-050)', borderRadius: 0 }}
            onClick={() => onZoomImage(coverUrl!)}
            aria-label={`Ampliar imagen: ${title}`}
          >
            <img src={coverUrl} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </button>
        ) : null}

        <Box p="xl">
          <Stack gap="md">
            <div>
              {dateString && (
                <Text size="xs" fw={700} tt="uppercase" c="blue.7" style={{ letterSpacing: '0.08em' }}>
                  {dateString}
                </Text>
              )}
              <Title order={2} mt={4} style={{ fontSize: 'clamp(22px, 3.4vw, 32px)', lineHeight: 1.15 }}>
                {title}
              </Title>
              {location && (
                <Group gap={6} mt={6} c="var(--text-2)">
                  <IconMapPin size={16} />
                  <Text size="sm">{location}</Text>
                </Group>
              )}
            </div>

            {body && (
              <Text size="md" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {body}
              </Text>
            )}

            {/* YouTube embed inside the body if there's also a cover image */}
            {ytId && coverUrl && (
              <Box mt="md" style={{ aspectRatio: '16/9', background: 'black' }}>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${ytId}`}
                  title={title}
                  style={{ width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            )}

            {/* Gallery */}
            {galleryRaw.length > 0 && (
              <Box mt="md">
                <Text fw={700} size="sm" mb="xs">Galería</Text>
                <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                  {galleryRaw.map((g: any, idx: number) => {
                    const u = g.url || g.publicUrl || null
                    if (!u) return null
                    return (
                      <button
                        key={idx}
                        type="button"
                        className="landing-zoom"
                        style={{ aspectRatio: '4/3', background: 'var(--ink-050)', borderRadius: 8 }}
                        onClick={() => onZoomImage(u)}
                        aria-label={g.alt ? `Ampliar imagen: ${g.alt}` : `Ampliar imagen ${idx + 1} de la galería`}
                      >
                        <img src={u} alt={g.alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                      </button>
                    )
                  })}
                </SimpleGrid>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Modal>
  )
}

const ImageZoomModal: React.FC<{ url: string | null; onClose: () => void }> = ({ url, onClose }) => {
  if (!url) return null
  return (
    <Modal
      opened={true}
      onClose={onClose}
      fullScreen
      padding={0}
      withCloseButton={false}
      className="landing-modal"
      styles={{
        body: { padding: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' },
        content: { background: 'transparent', boxShadow: 'none' }
      }}
    >
      <ActionIcon
        variant="filled"
        color="dark"
        size="lg"
        radius="xl"
        onClick={onClose}
        aria-label="Cerrar"
        style={{ position: 'fixed', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
      >
        <IconX size={20} stroke={2.2} color="white" />
      </ActionIcon>
      <img
        src={url}
        alt=""
        style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', cursor: 'zoom-out' }}
        onClick={onClose}
      />
    </Modal>
  )
}

// ============ Content to paint before the API answers ============

interface SiteData {
  content: SiteContent | null
  events: EventCard[]
  posts: BlogPost[]
  faqs: Faq[]
  masses: MassItem[]
  hours: HourItem[]
}

const SITE_CACHE_KEY = 'landing-site-v1'

/**
 * What the page shows the instant it loads. The API sleeps when the site is
 * idle and takes seconds to wake, so the first paint never waits for it: it
 * uses the copy this browser saved on its last visit or, for a first visit,
 * the copy built into the site (scripts/snapshot-site.mjs). Whichever is newer.
 */
function initialSiteData(): SiteData {
  const built = siteSnapshot as unknown as { taken_at: string; data: SiteData }
  try {
    const saved = JSON.parse(localStorage.getItem(SITE_CACHE_KEY) || 'null') as { saved_at?: string; data?: SiteData } | null
    if (saved?.data?.content && typeof saved.saved_at === 'string' && saved.saved_at > built.taken_at) return saved.data
  } catch {
    // Private mode or a damaged entry: the built-in copy is enough
  }
  return built.data
}

// ============ LandingPage ============

export const LandingPage: React.FC = () => {
  const [initial] = useState(initialSiteData)
  const [content, setContent] = useState<SiteContent | null>(initial.content)
  const [events, setEvents] = useState<EventCard[]>(initial.events || [])
  const [posts, setPosts] = useState<BlogPost[]>(initial.posts || [])
  const [faqs, setFaqs] = useState<Faq[]>(initial.faqs || [])
  const [masses, setMasses] = useState<MassItem[]>(initial.masses || [])
  const [hours, setHours] = useState<HourItem[]>(initial.hours || [])
  // Only true when there is nothing at all to show yet
  const [loading, setLoading] = useState(!initial.content)
  const [leadOpen, leadDisc] = useDisclosure(false)
  const [leadInterest, setLeadInterest] = useState<'donar' | 'voluntario'>('donar')
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null)
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null)

  useEffect(() => {
    // Same title as index.html, which crawlers and link previews read
    document.title = 'Cáritas La Esperanza | Centro Comunitario en Tlalpán, CDMX'
    // The page is already painted; this brings whatever changed since
    ;(async () => {
      try {
        const r = await fetch('/api/site/public')
        if (r.ok) {
          const result = await r.json()
          if (result.success && result.data) {
            const fresh = result.data as SiteData
            // Untouched content must not re-render the page (it would restart
            // the entrance animations for nothing)
            if (JSON.stringify(fresh) !== JSON.stringify(initial)) {
              setContent(fresh.content || null)
              setEvents(fresh.events || [])
              setPosts(fresh.posts || [])
              setFaqs(fresh.faqs || [])
              setMasses(fresh.masses || [])
              setHours(fresh.hours || [])
            }
            try {
              localStorage.setItem(SITE_CACHE_KEY, JSON.stringify({ saved_at: new Date().toISOString(), data: fresh }))
            } catch {
              // Storage full or unavailable: the next visit uses the built-in copy
            }
          }
        }
      } catch {
        // Offline or the API is down: the page keeps what it already shows
      } finally {
        setLoading(false)
      }
    })()
  }, [initial])

  const openLead = (interest: 'donar' | 'voluntario') => {
    setLeadInterest(interest)
    leadDisc.open()
  }

  // JSON-LD for SEO + AEO: one schema.org @graph (the centre with its hours,
  // FAQPage, events, posts) built by src/lib/site-prerender.ts. The build already
  // wrote the same graph into index.html from the saved content; this swaps it
  // for the live one, so the page never carries two.
  useEffect(() => {
    if (loading) return
    document.querySelectorAll('script[data-jsonld="site-graph"]').forEach(node => node.remove())
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-jsonld', 'site-graph')
    script.text = jsonForScript(buildSiteGraph({ content, events, posts, faqs, masses, hours }))
    document.head.appendChild(script)
    return () => { script.remove() }
  }, [loading, content, hours, faqs, events, posts, masses])

  // Preload the hero image as soon as we know its URL so it starts downloading
  // in parallel with the rest of the page render.
  useEffect(() => {
    if (!content?.hero_image_url) return
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = content.hero_image_url
    ;(link as any).fetchPriority = 'high'
    document.head.appendChild(link)
    return () => { document.head.removeChild(link) }
  }, [content?.hero_image_url])

  // Scroll-reveal: opacity + 16px translate-up, IntersectionObserver, reduced-motion guard.
  useEffect(() => {
    if (loading) return
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Sections are visible by default, so no-JS and reduced-motion readers get
    // the finished page. Only what is still below the fold gets armed — arming
    // an on-screen section would flash it out and back in.
    if (reducedMotion) return
    const els = Array.from(document.querySelectorAll<HTMLElement>('.reveal-section'))
    const fold = window.innerHeight * 0.9
    const belowFold = els.filter(el => el.getBoundingClientRect().top > fold) // read…
    belowFold.forEach(el => el.classList.add('reveal-armed'))                 // …then write
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const el = e.target as HTMLElement
          el.classList.remove('reveal-armed')
          el.classList.add('reveal-visible')
          obs.unobserve(el)
        }
      })
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' })
    belowFold.forEach(el => obs.observe(el))
    return () => obs.disconnect()
  }, [loading, events.length, posts.length, faqs.length])

  if (loading) {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size="lg" />
      </Box>
    )
  }

  return (
    <Box className="landing" style={{ background: 'var(--paper)' }}>
      <a className="landing-skip" href="#contenido">Saltar al contenido</a>
      <TopNav onOpenLead={openLead} content={content} />
      <Hero content={content} onOpenLead={openLead} />
      <div id="contenido" tabIndex={-1} />
      <AboutSection content={content} onZoomImage={(url) => setZoomImageUrl(url)} />
      <HoursSection content={content} hours={hours} onZoomImage={(url) => setZoomImageUrl(url)} />
      <MisasSection content={content} masses={masses} onZoomImage={(url) => setZoomImageUrl(url)} />
      <EventsSection events={events} onItemClick={(ev) => setSelectedItem({ type: 'event', item: ev })} />
      <BlogSection posts={posts} onItemClick={(p) => setSelectedItem({ type: 'post', item: p })} />
      <FAQSection faqs={faqs} />
      <ContactSection content={content} onOpenLead={openLead} />
      <Footer content={content} />
      <LeadModal opened={leadOpen} onClose={leadDisc.close} defaultInterest={leadInterest} />
      <DetailModal
        selected={selectedItem}
        onClose={() => setSelectedItem(null)}
        onZoomImage={(url) => setZoomImageUrl(url)}
      />
      <ImageZoomModal url={zoomImageUrl} onClose={() => setZoomImageUrl(null)} />
      <style>{`
        /* ---------- Tokens ----------------------------------------------
           One ink scale, one accent, one section tint. Everything on the
           page reads from these so a colour never gets invented inline. */
        .landing {
          --ink-900: #0f1d3a;
          --ink-800: #16264a;
          --ink-700: #1e3c72;
          --ink-600: #2a5298;
          --ink-100: #dbe4f3;
          --ink-050: #eef3fa;
          --paper:   #ffffff;
          --paper-2: #f5f7fb;
          --rule:    rgba(15, 29, 58, 0.10);
          --rule-2:  rgba(15, 29, 58, 0.18);
          --text:    #16203a;
          --text-2:  #4a5670;
          --accent:  #b3123f;
          --ease:    cubic-bezier(0.16, 1, 0.3, 1);
          color: var(--text);
          /* The reveals start 40px off to one side. Without this, on a phone that offset made the page wider
             than the screen (it could be dragged sideways) until every section had been scrolled into view. */
          overflow-x: clip;
        }

        /* ---------- Browser surfaces -------------------------------------
           The parts we did not draw still belong to the design: selection,
           caret, scrollbar, focus ring, link underlines. */
        .landing ::selection { background: rgba(30, 60, 114, 0.16); color: var(--ink-900); }
        .landing { caret-color: var(--ink-700); scrollbar-color: var(--ink-100) transparent; }
        .landing ::-webkit-scrollbar { width: 10px; height: 10px; }
        .landing ::-webkit-scrollbar-thumb { background: var(--ink-100); border-radius: 999px; }
        .landing ::-webkit-scrollbar-track { background: transparent; }
        .landing a { text-underline-offset: 0.18em; text-decoration-thickness: from-font; }
        .landing :focus-visible {
          outline: 2px solid var(--ink-600);
          outline-offset: 3px;
          border-radius: 6px;
        }
        .landing-on-dark :focus-visible { outline-color: #ffffff; }

        /* Pointer/touch ergonomics — no double-tap zoom delay, branded tap flash. */
        .landing a, .landing button, .landing [role="button"] {
          touch-action: manipulation;
          -webkit-tap-highlight-color: rgba(30, 60, 114, 0.12);
        }

        /* Numerals that get compared column-to-column line up. */
        .landing .num { font-variant-numeric: tabular-nums; font-feature-settings: "tnum" 1; }

        /* Anchor targets clear the fixed header instead of hiding under it. */
        .landing section[id], .landing [id="hero"] { scroll-margin-top: 88px; }

        /* Skip link — visible only once focused. */
        .landing-skip {
          position: absolute; left: 16px; top: -100px; z-index: 200;
          background: var(--ink-700); color: #fff;
          padding: 10px 16px; border-radius: 10px; font-weight: 600; font-size: 14px;
          text-decoration: none; transition: top 160ms var(--ease);
        }
        .landing-skip:focus { top: 16px; }

        /* ---------- Section rhythm --------------------------------------- */
        .landing-section {
          padding-top: clamp(56px, 9vw, 104px);
          padding-bottom: clamp(56px, 9vw, 104px);
        }
        /* Every section fills the viewport and centres its content. min-height,
           not height, so the long ones (Misas runs 7 days x 3 times) still grow
           instead of clipping. svh, not vh, so mobile browser chrome collapsing
           does not shift the layout mid-scroll. Skipped on short viewports
           (landscape phones) where a forced 100svh leaves no room to read. */
        @media (min-height: 600px) {
          .landing-section {
            min-height: 100svh;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .landing-section > * { width: 100%; }
        }
        .landing-section--tint { background: var(--paper-2); }
        .landing-heading {
          font-size: clamp(28px, 3.4vw, 44px);
          line-height: 1.08;
          letter-spacing: -0.03em;
          font-weight: 800;
          text-wrap: balance;
          color: var(--ink-900);
          margin: 0;
        }
        .landing-sub {
          max-width: 68ch;
          color: var(--text-2);
          line-height: 1.6;
          text-wrap: pretty;
          margin: 0;
        }
        .landing-rule { height: 1px; background: var(--rule); border: 0; margin: 0; }
        .landing-subhead {
          font-size: 15px; font-weight: 700; letter-spacing: -0.01em;
          color: var(--ink-900); margin: 0 0 4px;
        }

        /* Image slot with nothing uploaded yet: a quiet ink panel that states
           who this is, not a centred icon-and-caption card. */
        .landing-fallback {
          position: relative; display: flex; flex-direction: column; justify-content: flex-end;
          min-height: 280px; padding: 28px; border-radius: 14px; overflow: hidden;
          background: var(--ink-800); color: #fff;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
        }
        .landing-fallback::before {
          content: ''; position: absolute; left: 28px; right: 28px; top: 28px;
          height: 2px; background: var(--accent);
        }
        .landing-fallback__title {
          font-size: clamp(22px, 2.4vw, 30px); line-height: 1.1;
          letter-spacing: -0.03em; font-weight: 800; text-wrap: balance;
        }
        .landing-fallback__note { margin-top: 6px; font-size: 14px; color: rgba(255, 255, 255, 0.72); }

        /* ---------- Clickable cards --------------------------------------
           Real <button>s: keyboard-reachable, one elevation system (shadow
           only — a border under a soft shadow is the ghost card). */
        .landing-card {
          display: block; width: 100%; padding: 0; text-align: left;
          background: var(--paper); color: inherit; border: 0; border-radius: 14px;
          overflow: hidden; cursor: pointer;
          box-shadow: 0 1px 2px rgba(15, 29, 58, 0.06), 0 10px 22px -14px rgba(15, 29, 58, 0.30);
          transition: transform 220ms var(--ease), box-shadow 220ms var(--ease);
        }
        .landing-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 2px 4px rgba(15, 29, 58, 0.09), 0 20px 36px -16px rgba(15, 29, 58, 0.36);
        }
        .landing-card:active { transform: translateY(-1px); }
        .landing-card { position: relative; }
        .landing-card__media { position: relative; background: var(--ink-050); overflow: hidden; }
        /* Stretched link: one real <button> in the a11y tree, whole card hit-area. */
        .landing-card__link {
          all: unset; cursor: pointer; display: inline;
        }
        .landing-card__link::after {
          content: ''; position: absolute; inset: 0; border-radius: inherit;
        }
        .landing-card:has(.landing-card__link:focus-visible) {
          outline: 2px solid var(--ink-600); outline-offset: 3px;
        }
        .landing-card__link:focus-visible { outline: none; }
        .landing-card__media img { transition: transform 420ms var(--ease); }
        .landing-card:hover .landing-card__media img { transform: scale(1.035); }
        .landing-zoom { display: block; width: 100%; padding: 0; border: 0; background: none; cursor: zoom-in; border-radius: 14px; overflow: hidden; }

        /* ---------- Schedule rows ---------------------------------------- */
        .landing-sched { border-top: 1px solid var(--rule); }
        .landing-sched__row {
          display: grid; grid-template-columns: minmax(96px, auto) 1fr;
          gap: 16px; align-items: baseline;
          padding: 12px 12px 12px 0;
          border-bottom: 1px solid var(--rule);
        }
        .landing-sched__row--today { background: var(--ink-050); padding-left: 12px; border-radius: 8px; }
        .landing-sched__day { font-weight: 600; color: var(--ink-900); min-width: 0; }
        .landing-sched__times { text-align: right; min-width: 0; }
        .landing-sched--accent .landing-sched__row { border-bottom-color: rgba(179, 18, 63, 0.18); }
        .landing-sched--accent { border-top-color: rgba(179, 18, 63, 0.18); }

        .landing-deflist { margin: 0; border-top: 1px solid var(--rule); }
        .landing-deflist__row {
          display: grid; grid-template-columns: minmax(96px, auto) 1fr; gap: 16px;
          align-items: baseline; padding: 14px 0; border-bottom: 1px solid var(--rule);
        }
        .landing-deflist dt { color: var(--text-2); font-size: 14px; }
        .landing-deflist dd {
          margin: 0; min-width: 0; font-weight: 600; color: var(--ink-900);
          display: flex; align-items: baseline; gap: 8px;
        }
        .landing-deflist dd svg { flex: none; align-self: center; color: var(--ink-600); }
        .landing-deflist dd > span, .landing-deflist dd > a { min-width: 0; overflow-wrap: anywhere; }

        /* ---------- Motion -----------------------------------------------
           Default is visible. JS arms a section only while it is still below
           the fold, so no-JS and reduced-motion readers get the finished page.
           The section itself never fades — its parts do, each on its own path,
           so no two sections enter the same way. */

        /* Headings and blocks rise. */
        .landing .r-item { transition: opacity 700ms var(--ease), transform 700ms var(--ease); }
        .landing .reveal-armed .r-item { opacity: 0; transform: translateY(26px); }
        .landing .reveal-visible .r-item { opacity: 1; transform: none; }

        /* Images wipe in from their own side rather than just fading. */
        .landing .r-left, .landing .r-right {
          transition: opacity 820ms var(--ease), transform 820ms var(--ease), clip-path 820ms var(--ease);
        }
        .landing .reveal-armed .r-left {
          opacity: 0; transform: translateX(-40px); clip-path: inset(0 0 0 22%);
        }
        .landing .reveal-armed .r-right {
          opacity: 0; transform: translateX(40px); clip-path: inset(0 22% 0 0);
        }
        .landing .reveal-visible .r-left,
        .landing .reveal-visible .r-right {
          opacity: 1; transform: none; clip-path: inset(0 0 0 0);
        }

        /* Repeated rows and cards cascade in sequence. */
        .landing .r-stagger > * { transition: opacity 560ms var(--ease), transform 560ms var(--ease); }
        .landing .reveal-armed .r-stagger > * { opacity: 0; transform: translateY(18px); }
        .landing .reveal-visible .r-stagger > * { opacity: 1; transform: none; }
        .landing .reveal-visible .r-stagger > *:nth-child(1) { transition-delay: 40ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(2) { transition-delay: 110ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(3) { transition-delay: 180ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(4) { transition-delay: 250ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(5) { transition-delay: 320ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(6) { transition-delay: 390ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(7) { transition-delay: 460ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(8) { transition-delay: 530ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(9) { transition-delay: 600ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(10) { transition-delay: 670ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(11) { transition-delay: 740ms; }
        .landing .reveal-visible .r-stagger > *:nth-child(12) { transition-delay: 810ms; }

        /* The one authored moment: the hero resolves out of soft focus. */
        @keyframes landingHeroIn {
          from { opacity: 0; transform: translateY(20px); filter: blur(12px); }
          to   { opacity: 1; transform: none;             filter: blur(0); }
        }
        .landing-hero-in { animation: landingHeroIn 900ms var(--ease) both; }
        .landing-hero-in--2 { animation-delay: 110ms; }
        .landing-hero-in--3 { animation-delay: 210ms; }

        .mobile-carousel::-webkit-scrollbar { display: none; }
        .mobile-carousel { -webkit-overflow-scrolling: touch; scrollbar-width: none; }

        /* ---------- Overlays ---------------------------------------------
           Scroll inside a modal must not chain to the page behind it, and
           iOS must not zoom the viewport when a 14px input takes focus. */
        .landing-modal .mantine-Modal-body,
        .landing-modal .mantine-Modal-content,
        .landing-drawer .mantine-Drawer-body { overscroll-behavior: contain; }
        @media (max-width: 767px) {
          .landing-modal input,
          .landing-modal textarea,
          .landing-modal select { font-size: 16px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .landing .reveal-section,
          .landing .reveal-armed,
          .landing .reveal-armed .r-item,
          .landing .reveal-armed .r-left,
          .landing .reveal-armed .r-right,
          .landing .reveal-armed .r-stagger > * {
            opacity: 1; transform: none; clip-path: none; transition: none;
          }
          .landing-hero-in { animation: none; }
          .landing-card, .landing-card__media img { transition: none; }
          .landing-card:hover { transform: none; }
          .landing-card:hover .landing-card__media img { transform: none; }
          .mobile-carousel { scroll-behavior: auto; }
        }
      `}</style>
    </Box>
  )
}

export default LandingPage
